import { Box, Center, Circle, Heading, Spacer, Stack, Text } from '@chakra-ui/react'
import { sortBy } from 'lodash-es'
import 'swiper/swiper-bundle.css'

import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'

import FeaturedFeedbackGrid from '@/components/Feedback/FeaturedFeedbackGrid'
import type { FeaturedFeedback } from '@/components/Feedback/types'
import {
  featuredFeedbackDetailsFromPostFeedbackQuery,
  featuredFeedbackDetailsFromSessionFeedbackQuery,
  featuredFeedbackDetailsFromTutorAuditQuery,
} from '@/components/Worlds/transforms/detailsFromQueries'

function ProfileFeaturedFeedback({
  user,
  limit = 4,
  compact = false,
  hideIfEmpty = false,
  ButtonComponent = () => <Spacer />,
}: {
  user: Query
  limit?: number
  compact?: boolean
  hideIfEmpty?: boolean
  ButtonComponent?: () => JSX.Element
}): JSX.Element | null {
  const currentUser = useUser()
  const firstName = user(UserQ.first_name) ?? ''
  const isUserTutor = user(UserQ.hasTutorRole()) ?? false

  const featuredTutorAudits: FeaturedFeedback[] = user
    .many(UserQ.getTutorAuditsFeatured())
    .map(featuredFeedbackDetailsFromTutorAuditQuery)

  const featuredSessionFeedbacks: FeaturedFeedback[] = user
    .many(UserQ.getSessionFeedbacksFeatured())
    .map(featuredFeedbackDetailsFromSessionFeedbackQuery)

  const featuredPostFeedbacks: FeaturedFeedback[] = user
    .many(UserQ.getPostFeedbacksFeatured())
    .map(featuredFeedbackDetailsFromPostFeedbackQuery)

  const featuredFeedback = sortBy(
    [...featuredTutorAudits, ...featuredSessionFeedbacks, ...featuredPostFeedbacks],
    feedback => -feedback.date.toMillis()
  )

  const totalCount = featuredFeedback.length

  const viewingOwnProfile = user(UserQ.id) === currentUser.id

  if (!isUserTutor || (hideIfEmpty && totalCount === 0)) {
    return null
  }
  return (
    <Box>
      <Stack spacing={2} direction="row" justify="space-between" align="flex-start" mb={3}>
        <Stack direction="row" spacing="12px" align="center">
          <Heading size="xl">Featured Feedback</Heading>
          {totalCount === 0 ? null : (
            <Circle
              fontWeight="semibold"
              size="30px"
              bg="primary.solid"
              color="primary.contrast"
              display={['none', 'auto']}
            >
              {totalCount}
            </Circle>
          )}
        </Stack>
        <Spacer />
        <ButtonComponent />
      </Stack>
      {totalCount === 0 ? (
        <Center
          bg="bg.subtle"
          borderRadius={8}
          p={12}
          border="2px dashed"
          borderColor="border.emphasized"
        >
          <Text align="center" color="fg.muted" pb={4}>
            {firstName} hasn&apos;t featured any feedback yet!
          </Text>
        </Center>
      ) : (
        <Box pl="14px">
          <FeaturedFeedbackGrid
            compact={compact}
            featuredFeedback={featuredFeedback}
            viewingOwnProfile={viewingOwnProfile}
            limit={limit}
          />
        </Box>
      )}
    </Box>
  )
}

export default ProfileFeaturedFeedback
